import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Star as StarIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import { useRecipe } from '../../context/RecipeContext';
import flexitarianBackground from '../../images/flexitarian.png';
import flexKickstartImage from '../../images/flex-kickstart.jpeg';
import flexitarian1Image from '../../images/Flexitarian-1.avif';
import veganCafeImage from '../../images/vegan-cafe.jpg';

const PAGE_URL = 'https://createadish.com/dietary-guide/flexitarian';
const PAGE_TITLE = 'Flexitarian Diet Guide: Flexible Plant-Based Eating - Create A Dish';
const PAGE_DESCRIPTION = 'A "flexible vegetarian" approach that encourages mostly plant-based meals while allowing for the occasional consumption of meat and animal products.';
const PAGE_IMAGE = 'https://createadish.com/images/flexitarian-og.png';

const Flexitarian: React.FC = () => {
  const navigate = useNavigate();
  const { setMode } = useRecipe();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGetStarted = () => {
    setMode('generate');
    navigate('/composer');
  };

  return (
    <>
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <meta name="keywords" content="flexitarian diet, plant-based, vegetarian, sustainable eating, flexible diet, semi-vegetarian" />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:image" content={PAGE_IMAGE} />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={PAGE_URL} />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
        <meta name="twitter:image" content={PAGE_IMAGE} />
        
        <link rel="canonical" href={PAGE_URL} />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Flexitarian Diet Guide: Flexible Plant-Based Eating",
            "description": PAGE_DESCRIPTION,
            "image": PAGE_IMAGE,
            "author": {
              "@type": "Organization",
              "name": "Create A Dish"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Create A Dish",
              "logo": {
                "@type": "ImageObject",
                "url": "https://createadish.com/logo.png"
              }
            },
            "datePublished": "2025-12-24",
            "dateModified": "2025-12-24",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": PAGE_URL
            },
            "keywords": "Sustainability, Vegetarian, Flexible"
          })}
        </script>
      </Helmet>

      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', maxWidth: '1200px', mx: 'auto' }}>
        <Box
          sx={{
            height: { xs: '40vh', md: '50vh' },
            backgroundImage: `url(${flexitarianBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(1px)',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              px: 2,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                mb: 2,
              }}
            >
              Flexitarian Diet
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                mb: 1,
              }}
            >
              Flexible Plant-Based Eating
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2, md: 4 }, flexGrow: 1 }}>
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            sx={{ mb: 3 }}
            aria-label="breadcrumb"
          >
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate('/resources')}
              sx={{ cursor: 'pointer' }}
            >
              Resources
            </Link>
            <Link
              underline="hover"
              color="inherit"
              onClick={() => navigate('/dietary-guide')}
              sx={{ cursor: 'pointer' }}
            >
              Dietary Guide
            </Link>
            <Typography color="text.primary">Flexitarian Diet</Typography>
          </Breadcrumbs>

          <Typography
            variant="h4"
            component="h2"
            sx={{
              textAlign: 'center',
              mb: 4,
              fontWeight: 'bold',
              color: 'primary.main'
            }}
          >
            The Flexitarian Diet
          </Typography>

          <Typography
            variant="body1"
            sx={{
              mb: 4,
              textAlign: 'center',
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: { xs: '1rem', md: '1.1rem' }
            }}
          >
            A "flexible vegetarian" approach that encourages mostly plant-based meals while allowing for the occasional consumption of meat and animal products.
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              #Sustainability
            </Typography>
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              #Vegetarian
            </Typography>
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
              #Flexible
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
            Welcome to CreateADish.com's guide to the Flexitarian Diet, a flexible approach to eating that prioritizes plant-based foods while allowing occasional meat and animal products. Coined as a blend of "flexible" and "vegetarian," this diet offers a sustainable way to reduce meat intake without strict rules, making it ideal for those transitioning to healthier, eco-friendly habits. Our AI recipe generator can help you craft personalized flexitarian meals—input preferences like "mostly plant-based with occasional chicken" for delicious, balanced ideas.
          </Typography>
          <Box component="img" src={flexKickstartImage} alt="Flexitarian kickstart" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Historical Background
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The flexitarian diet emerged in the early 1990s but gained popularity through registered dietitian Dawn Jackson Blatner's 2009 book, "The Flexitarian Diet." It draws from ancient plant-based traditions but adapts to modern needs, influenced by growing awareness of environmental sustainability and health benefits in the 21st century.
            </Typography>
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Key Components and Foods
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: { xs: '1rem', md: '1.1rem' } }}>
              The flexitarian diet focuses on whole, minimally processed foods with plants at the core. There's no rigid calorie counting—just aim for 80-90% plant-based meals.
            </Typography>
            
            <Typography variant="h5" component="h4" sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
              Core Foods to Emphasize
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Fruits and Vegetables</strong>: Berries, apples, leafy greens, broccoli, carrots, and tomatoes.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Whole Grains</strong>: Quinoa, brown rice, oats, barley, and whole-wheat bread.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Plant Proteins</strong>: Legumes (beans, lentils, chickpeas), nuts, seeds, tofu, tempeh.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Healthy Fats</strong>: Avocados, olive oil, nuts, and seeds.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Occasional Animal Proteins</strong>: Lean meats, chicken, fish, 2-3 times per week or less.</Typography>
            </Box>
            <Box component="img" src={flexitarian1Image} alt="Flexitarian foods" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Health Benefits
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Weight Management</strong>: High-fiber plant foods promote fullness.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Heart Health</strong>: Reduced saturated fat intake lowers cholesterol.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Diabetes Prevention</strong>: Plant-based meals stabilize blood sugar.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Sustainability</strong>: Environmentally friendly by cutting meat consumption.</Typography>
            </Box>
            <Box component="img" src={veganCafeImage} alt="Plant-based cafe" sx={{ width: '100%', maxWidth: 800, mx: 'auto', my: 3, borderRadius: 2, boxShadow: 3, display: 'block' }} />
          </Box>

          <Box sx={{ mb: 6 }}>
            <Typography variant="h4" component="h3" sx={{ mb: 3, fontWeight: 'bold', color: 'primary.main' }}>
              Tips for Adopting the Flexitarian Diet
            </Typography>
            <Box component="ul" sx={{ mb: 3, pl: 3 }}>
              <Typography component="li" sx={{ mb: 1 }}><strong>Ease In</strong>: Start with Meatless Mondays and gradually increase plant-based days.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Experiment</strong>: Try plant proteins like lentils in place of meat.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Leverage AI</strong>: Generate recipes on CreateADish.com.</Typography>
              <Typography component="li" sx={{ mb: 1 }}><strong>Focus on Sustainability</strong>: Choose local, seasonal produce.</Typography>
            </Box>
          </Box>

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h5"
              component="h3"
              sx={{
                mb: 3,
                fontWeight: 'bold',
                color: 'primary.main'
              }}
            >
              Ready to Create Your Perfect Dish?
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<StarIcon />}
              onClick={handleGetStarted}
              sx={{
                minWidth: 200,
                fontSize: '1.1rem',
                py: 1.5,
                px: 4,
              }}
            >
              Get Started
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', py: 3, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary">
              © 2025 Create A Dish. Made with ❤️ for food lovers everywhere.
            </Typography>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default Flexitarian;
